import ResourceTable from "../resource-table";
import {
    checkPerm,
    classNames,
    getProp,
    resourceIsCreated,
    resourceIsDeleted,
    resourceIsUpdated
} from "../../util/util-helpers";
import Resources from "../../../data/services/resources";
import CheckCircleIcon from "@heroicons/react/24/outline/CheckCircleIcon";
import {ArrowPathIcon} from "@heroicons/react/24/outline";
import NoRecordsTable from "../no-records-found/no-records-table";
import TableCard from "../resource-table/table-components/table-card";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Field, FieldsManager} from "../../../data/services/fields";
import LocalStorage from "../../../util/localStorage";
import ModalConfirm from "../modal/modal-confirm";
import {
    clearUpdateStateSecondResource,
    createSecondResource,
    deleteSecondResource,
    getSecondResource,
    updateSecondResource
} from "../../../data/actions/secondResource";
import {DEFAULT_LIMIT, DEFAULT_OFFSET, DEFAULT_SORT, DELETE_PERM, UPDATE_PERM} from "../../util/util-consts";
import ContactInfoDialog from "../modal/contact-info-dialog";
import useQueryNew from "../../../hooks/use-query-new";
import useTableOptions from "../../hooks/use-table-options";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import TableCardFooter from "../resource-table/table-components/table-card-footer";
import Pagination from "../resource-table/table-components/pagination";
import Button from "../button";
import {CREATE_PERM, DEFAULT_DATABASE_DATETIME_FORMAT} from "../../../util/util-constants";
import ContactSelectCreateModal from "../modal/contact-select-create-modal";
import moment from "moment/moment";

// QueryFields

export default function DriverDispatchList({translate, organizationID, onDispatcherChange}) {

    const pagePath = "driver-dispatch-list"
    const tableDefaults = {
        behaviour: {
            rowSelect: true,
            canAdjustWidth: true,
            hasMenu: false
        },
        style: {
            condensed: true,
            isGPUAccelerated: true,
            isRounded: true
        },
        version: "1"
    }

    const dispatch = useDispatch();

    const contactResource = useSelector(state => state.contactResource);
    const resource = useSelector(state => state.secondResource);
    const dataItems = getProp(resource, "data.list", []);
    const count = getProp(resource, "data.count", 0);
    const isLoading = getProp(resource, "isLoading", true);


    const [queryFields, setQueryFields] = useQueryNew(getQueryFields(), pagePath);
    const query = FieldsManager.getFieldKeyValues(queryFields);
    query.id = organizationID;
    query.IsEmployee = 1;

    const [selectedItem, setSelectedItem] = useState(false);
    const [isDeleteConfirmDialogOpen, setIsDeleteConfirmDialogOpen] = useState(false);
    const [isPrimaryConfirmDialogOpen, setIsPrimaryConfirmDialogOpen] = useState(false);
    const [isContactInfoDialogOpen, setIsContactInfoDialogOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const [tableOptions] = useTableOptions(getFields(), pagePath, tableDefaults, translate);

    function fetchContacts() {
        dispatch(getSecondResource({
            user: LocalStorage.get("user"),
            resource: Resources.DriverDispatch,
            query: query,
        }));
    }

    function handleSetPrimaryClick() {
        dispatch(updateSecondResource({
            user: LocalStorage.get('user'),
            params: {ContactInDriverID: selectedItem.ContactInDriverID},
            resource: Resources.DriverDispatchPrimary,
            piggyResource: Resources.DriverDispatch,
            query: query,
            errorMessage: true,
            successMessage: `Driver ${selectedItem.FirstName} ${selectedItem.LastName} successfully set as primary contact!`
        }));
    }

    function handleDeleteDispatcherClick() {
        dispatch(deleteSecondResource({
            user: LocalStorage.get('user'),
            query: {
                ContactID: selectedItem.ContactID,
                ...query
            },
            secondQuery: {
                ContactInDriverID: selectedItem.ContactInDriverID,
                ...query
            },
            errorMessage: true,
            successMessage: translate(`text.contact_deleted_from_${Resources.DriverDispatch}`, [`${selectedItem.FirstName} ${selectedItem.LastName}`]),
            resource: Resources.DriverDispatch,
            secondPiggyResource: Resources.DriverDispatch
        }))
    }

    function handleQueryInputChange(name, value) {
        const queryFieldsUpdate = FieldsManager.updateField(queryFields, name, value);

        if ("sortBy" === name) {
            if (value === queryFieldsUpdate?.sortBy?.value) {
                queryFieldsUpdate.sort.value = queryFieldsUpdate.sort.value === 'ASC' ? 'DESC' : 'ASC'
            }
        }

        setQueryFields(queryFieldsUpdate);
    }

    function archiveResourceConfirm(item) {
        setSelectedItem(item);
        setIsDeleteConfirmDialogOpen(true);
    }

    function handleShowDetails(item) {
        setSelectedItem(item);
        setIsContactInfoDialogOpen(true);
    }

    function confirmPrimaryContactChange(item) {
        setSelectedItem(item);
        setIsPrimaryConfirmDialogOpen(true);
    }

    function getFields() {
        return {
            FirstName: new Field('FirstName', '', [], false, 'custom', {
                label: "name",
                columnName: 'DispatcherName',
                render: (item) => item.FirstName + ' ' + item.LastName
            }),
            Email: new Field('Email', '', []),
            SetDate: new Field('SetDate', '', [''], false, 'date'),
            Notes: new Field('Notes', '', []),
            IsPrimary: new Field('IsPrimary', '', [], false, 'checkbox'),
        }
    }

    function getQueryFields() {
        return {
            query: new Field('query', '', [''], false, 'search', {
                hasActiveBadge: true,
                labelType: "float"
            }, {placeholder: translate("text.search")}),
            sort: new Field('sort', DEFAULT_SORT, [''], false, 'hidden'),
            sortBy: new Field('sortBy', "FirstName", [''], false, 'hidden'),
            offset: new Field('offset', DEFAULT_OFFSET, [''], false, 'hidden'),
            limit: new Field('limit', DEFAULT_LIMIT, [''], false, 'select', {
                hideLabel: true,
                labelType: "float"
            }, {
                hasPortal: true,
                placeholder: "100",
                menuPlacement: "top"
            })
        }
    }

    useEffect(() => {
        fetchContacts();
    }, [queryFields]);

    useEffect(() => {
        if (onDispatcherChange && (resource.resource === Resources.DriverDispatch || resource.resource === Resources.DriverDispatchPrimary) && (resourceIsCreated(resource) || resourceIsUpdated(resource) || resourceIsDeleted(resource))) {
            dispatch(clearUpdateStateSecondResource());
            onDispatcherChange(selectedItem.ContactInDriverID);
        }
    }, [onDispatcherChange, resource])

    return <>
        <div className="my-2 flex gap-2">
            <button className="btn btn-header ml-auto" onClick={fetchContacts}>
                <ArrowPathIcon className={classNames("w-5 h-5", isLoading ? "animate-spin" : undefined)}/>
            </button>

            <Button
                appearance="primary"
                onClick={() => setIsAddModalOpen(true)}
                hasPerm={checkPerm(Resources.DriverDispatch, CREATE_PERM)}
            >
                {translate("btn.add/create")}
            </Button>
        </div>

        <TableCard addClass={'relative'}>
            <ResourceTable
                addClass="rounded-card"
                data={dataItems}
                count={count}

                fields={getFields()}
                translate={translate}
                isLoading={isLoading}

                queryFields={queryFields}
                onSortChange={(fieldName) => handleQueryInputChange('sortBy', fieldName)}

                onView={checkPerm(Resources.Contacts, UPDATE_PERM) ? handleShowDetails : null}
                options={tableOptions}
                actions={[
                    {
                        action: confirmPrimaryContactChange,
                        customContent: (item) => <CheckCircleIcon
                            className={classNames("size-5", !item.IsPrimary ? "text-tm-gray-500" : "text-green-600")}/>,
                        label: false,
                        title: translate('btn.set_contact_as_primary'),
                        disabled: (item) => item?.IsPrimary,
                        class: false,
                        iconClass: false,
                        order: 40
                    },
                    {
                        order: 50,
                        action: archiveResourceConfirm,
                        disabled: (item) => item?.IsPrimary,
                        customContent: (item) => <TrashIcon
                            className={classNames("size-5", item.IsPrimary ? "text-tm-gray-400" : "text-tm-gray-600")}/>,
                        visible: () => checkPerm(Resources.DriverDispatch, DELETE_PERM),
                        title: (item) => item.IsPrimary ? translate("text.primary_contact_delete") : translate("btn.delete")
                    },
                ]}
            />

            <NoRecordsTable
                show={(dataItems.length === 0) && !isLoading}
                canCreate={false}
                title={translate("text.no_matching_records")}
                className={"pt-16 px-6"}
            />

            <TableCardFooter
                show={!(!dataItems.length && !isLoading)}
            >
                <Pagination
                    count={count}
                    isLoading={isLoading}
                    handleQueryChange={(name, value) => handleQueryInputChange(name, value)}
                    queryFields={queryFields}
                    translate={translate}
                />
            </TableCardFooter>
        </TableCard>

        <ModalConfirm
            type={"info"}
            title={translate("text.Confirm")}
            show={isPrimaryConfirmDialogOpen}
            text={translate('text.confirm_set_primary_contact')}
            onClose={() => setIsPrimaryConfirmDialogOpen(false)}
            buttonLabel={translate("btn.confirm")}
            closeButtonLabel={'Cancel'}
            translate={translate}
            onConfirm={() => {
                handleSetPrimaryClick();
                setIsPrimaryConfirmDialogOpen(false);
            }}
        />

        <ModalConfirm
            type={"info"}
            title={translate("text.Confirm")}
            show={isDeleteConfirmDialogOpen}
            onClose={() => setIsDeleteConfirmDialogOpen(false)}
            buttonLabel={translate("btn.confirm")}
            closeButtonLabel={'Cancel'}
            translate={translate}
            onConfirm={() => {
                handleDeleteDispatcherClick()
                setIsDeleteConfirmDialogOpen(false);
            }}
        >
            {translate('text.confirm_delete_contact_from_drivers/contacts', [`${selectedItem.FirstName} ${selectedItem.LastName}`])}
        </ModalConfirm>

        <ContactInfoDialog
            show={isContactInfoDialogOpen}
            translate={translate}
            contactID={selectedItem?.ContactID}
            handleClose={() => setIsContactInfoDialogOpen(false)}
        />


        {isAddModalOpen && (
            <ContactSelectCreateModal
                show={isAddModalOpen}
                title={'Add Contact'}
                widthClass={'max-w-7xl'}
                disableContactCreate={false}
                queryFilterFields={queryFields}
                disableMemberOfOrganization={false}
                selectableCollumn={true}
                systemUserQuery={{IsDispatcher: 1}}

                dialogTableSelectFields={{
                    FirstName: new Field('FirstName', '', [], false, 'custom', {
                        columnName: 'DispatcherName',
                        render: (item) => item.FirstName + ' ' + item.LastName
                    }),
                    Email: new Field('Email', '', []),
                }}

                // ADDITIONAL FIELDS
                additionalFields={{
                    SetDate: new Field('SetDate', moment().format(DEFAULT_DATABASE_DATETIME_FORMAT), ['empty'], false, 'date', {addContainerClass: 'relative w-40'}),
                    DepartmentID: new Field("DepartmentID", 2, ['empty'], false, 'hidden', {
                        addContainerClass: 'relative md:col-span-2 mb-3'
                    }),
                    Notes: new Field("Notes", '', []),
                }}

                // HELPERS
                query={query}
                ExcludeIDs={dataItems.map(it => it.ContactID)}

                // HANDLERS
                onClose={() => setIsAddModalOpen(false)}

                handleAddExistingContact={(item, additionalFields) => {
                    dispatch(createSecondResource({
                        user: LocalStorage.get('user'),
                        params: {
                            ContactID: item.ContactID,
                            id: organizationID,
                            ...additionalFields,
                            ContactLocationNotes: additionalFields.Notes
                        },
                        query: query,
                        resource: Resources.DriverDispatch,
                        piggyResource: Resources.DriverDispatch,
                        errorMessage: true,
                        successMessage: `You added ${item.FirstName} ${item.LastName}`,
                    }));
                }}

                handleCreateNewContact={(fields, phones, files, additionalFields) => {
                    dispatch(createSecondResource({
                        user: LocalStorage.get('user'),
                        params: Object.assign({},
                            {
                                ...additionalFields,
                                ...fields,
                                Phones: phones,
                                HighImportance: fields?.HighImportance?.value ? 1 : 0,
                                Roles: fields.Roles ?? [],
                            }, {
                                ContactID: -1,
                                id: organizationID
                            },
                        ),
                        errorMessage: true,
                        successMessage: `You created and added ${fields.FirstName} ${fields.LastName}`,
                        query: query,
                        resource: Resources.DriverDispatch,
                        piggyResource: Resources.DriverDispatch,
                        file: [files],
                        fileResource: Resources.ContactImage
                    }));
                }
                }

                translate={translate}
                contactResource={contactResource}
                dispatch={dispatch}
                secondResource={resource}
            />
        )}
    </>
}